import React, { useState, useEffect, useRef } from 'react';
import { App, Modal, Input, Radio, Select } from 'antd';
import type { InputRef } from 'antd';
import type { Response } from '@/api/axiosInstance';
import type { UploadFileResourceResponse } from '@/api/file';
import { fileService } from '@/utils/filesService';
import useFileStore from '@/utils/fileStore';
import { FileTypeEnum, FileTypeToExtensionMap } from '@/types/fileTypeEnum';
import { useTranslation } from 'react-i18next';

interface CreateFileDialogProps {
    visible: boolean;
    type: 'file' | 'folder' | undefined;
    targetPath: string;
    onCancel: () => void;
    onSuccess: () => void;
}

const CreateFileDialog: React.FC<CreateFileDialogProps> = ({
    visible,
    type,
    targetPath,
    onCancel,
    onSuccess
}) => {
    const { message } = App.useApp();
    const [newName, setNewName] = useState<string>('');
    const [fileType, setFileType] = useState<FileTypeEnum>(FileTypeEnum.Mlatom);
    const [isOthersSelected, setIsOthersSelected] = useState<boolean>(false);
    const { t } = useTranslation('common');

    const markFileAsNew = useFileStore((state) => state.markFileAsNew);

    const inputRef = useRef<InputRef>(null);
    const openFile = useFileStore((state) => state.openFile);
    const updateFolder = useFileStore((state) => state.updateFolder);
    // Reset form when dialog opens
    useEffect(() => {
        if (visible && type === 'file') {
            setFileType(FileTypeEnum.Mlatom);
            setIsOthersSelected(false);
            setNewName('.inp');
            setTimeout(() => {
                inputRef.current?.focus({
                    cursor: 'start',
                });
            }, 100);
        } else if (visible) {
            setNewName('');
        }
    }, [visible, type]);

    useEffect(() => {
        if (type === 'file') {
            const suffix = FileTypeToExtensionMap[fileType];
            setNewName((prevName) => {
                // If previous name already has the correct suffix, do nothing
                if (prevName.endsWith(suffix)) {
                    return prevName;
                }
                // Remove any existing known suffix and add the new one
                const nameWithoutSuffix = prevName.replace(/(\.inp|\.py|\.txt|\.xyz|\.com)$/, '');
                return nameWithoutSuffix + suffix;
            });
        }
    }, [fileType, type]);

    // Auto focus input when dialog opens
    useEffect(() => {
        if (visible) {
            setTimeout(() => {
                const input = document.getElementById('create-dialog-name-input');
                if (input) {
                    input.focus();
                }
            }, 100);
        }
    }, [visible]);

    const handleCreate = async () => {
        const createType = type === "folder" ? "Folder" : "File";

        if (!newName) {
            message.warning(type === 'folder' ? t('create_file.folder_name_empty') : t('create_file.file_name_empty'));
            return;
        }

        if (!targetPath && !fileService.baseHome) {
            message.warning(type === 'folder' ? t('create_file.create_folder_failed') : t('create_file.create_file_failed'));
            return;
        }

        let res: Response<UploadFileResourceResponse> | undefined = undefined;

        try {
            const path = targetPath ?? fileService.baseHome;

            if (type === 'folder') {
                res = await fileService.createFolder(path, newName);
            } else {
                // Add file extension based on file type
                let fileName = newName;

                res = await fileService.createFile(path, fileName);
            }

            if (res?.ok) {
                if (res.data?.hasConflicts) {
                    message.error(t('create_file.already_exists'));
                } else {
                    message.success(type === 'folder' ? t('create_file.create_folder_success') : t('create_file.create_file_success'));

                    // Mark the newly created file or folder as new
                    if (res.data?.successFiles && res.data.successFiles.length > 0) {
                        const createdItemPath = res.data.successFiles[0].path;

                        if (type === 'file') {
                            await updateFolder(path);
                            openFile(createdItemPath);
                        } else {
                            markFileAsNew(createdItemPath);
                        }
                    }

                    onSuccess();
                }
            } else {
                message.error(res?.message ?? (type === 'folder' ? t('create_file.create_folder_failed') : t('create_file.create_file_failed')));
            }
        } catch (e) {
            console.log(e);
            message.error(type === 'folder' ? t('create_file.create_folder_failed') : t('create_file.create_file_failed'));
        }
    };

    const handleRadioChange = (e: any) => {
        const value = e.target.value;
        if (value === 'others') {
            setIsOthersSelected(true);
            setFileType(FileTypeEnum.XYZ);
        } else {
            setIsOthersSelected(false);
            setFileType(value);
        }
    };

    const handleCancel = () => {
        setNewName('');
        setFileType(FileTypeEnum.Mlatom);
        setIsOthersSelected(false);
        onCancel();
    };

    if (!type) return null;

    return (
        <Modal
            title={type === 'folder' ? t('create_file.create_folder_title') : t('create_file.create_file_title')}
            open={visible}
            onCancel={handleCancel}
            onOk={handleCreate}
            width={600}
            destroyOnClose
        >
            <p>{t('create_file.location')} {targetPath ?? fileService.baseHome}</p>

            {type === 'file' && (
                <div className='mt-2'>
                    <Radio.Group value={isOthersSelected ? "others" : fileType} onChange={handleRadioChange}>
                        <Radio value={FileTypeEnum.Mlatom}>{t('create_file.mlatom_input')}</Radio>
                        <Radio value={FileTypeEnum.Python}>{t('create_file.python')}</Radio>
                        <Radio value={FileTypeEnum.Text}>{t('create_file.text')}</Radio>
                        <Radio value="others">{t('create_file.others')}{isOthersSelected && <span>:</span>}</Radio>
                    </Radio.Group>
                    <div className="inline-block">
                        <Select
                            value={isOthersSelected ? fileType : FileTypeEnum.XYZ}
                            disabled={!isOthersSelected}
                            onChange={e => setFileType(e as FileTypeEnum)}
                            style={{ width: 150 }}
                            options={[
                                { value: FileTypeEnum.XYZ, label: t('create_file.structure_xyz') },
                                { value: FileTypeEnum.GAUSSIAN, label: t('create_file.gaussian_input') },
                            ]}
                        />
                    </div>
                </div>
            )}

            <Input
                className='mt-2'
                placeholder={type === 'folder' ? t('create_file.enter_folder_name') : t('create_file.enter_file_name')}
                id="create-dialog-name-input"
                ref={inputRef}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onPressEnter={handleCreate}
            />
        </Modal>
    );
};

export default CreateFileDialog;
